import { useMemo } from 'react'

import { useAppSelector } from '@/app/hooks'
import { useGetProjectConfigQuery } from '@/apis/v2Api'
import type { V2RangeArgs } from '@/apis/v2Mappers'
import { periodToRange, todayIn, type Period } from '@/utils/dates'

/**
 * The selected project (guaranteed by <ProjectGate>) — pages under the gate
 * never render without one.
 */
export function useProjectId(): string {
    return useAppSelector(s => s.project.selectedId) ?? ''
}

/**
 * "Today" in the project's timezone (from the v2 config; the browser's until it loads).
 * Report dates are calendar days in that timezone, like the API's own defaults.
 */
export function useProjectToday(): string {
    const projectId = useProjectId()
    const { data } = useGetProjectConfigQuery(projectId, { skip: !projectId })
    return todayIn(data?.config.timezone)
}

/** Range + project + sub-tenant filter for a v2 report. */
export function useReportArgs(period: Period): V2RangeArgs {
    const projectId = useProjectId()
    const subTenant = useAppSelector(s => s.project.subTenant)
    const today = useProjectToday()
    return useMemo(
        () => ({ projectId, subTenant, ...periodToRange(period, today) }),
        [projectId, subTenant, period, today],
    )
}
